import { useEffect, type RefObject } from 'react';
import { clsx } from 'clsx';
import type { TrackingPropsInterface } from '../utils/enums/enums';
import { constants } from '../constants/ApplicationConstants';
import { getClickTrackValue, trackImpression } from '../utils/TrackingPropertyUtils';

export interface UseTrackingParameters {
  ref: RefObject<HTMLElement>;
  trackingProperties?: TrackingPropsInterface;
  isExternal?: boolean;
  custom_click_track_value?: string;
  preTrackVal?: string;
  text?: string;
}

export function useTracking({
  ref,
  trackingProperties = {},
  isExternal = false,
  custom_click_track_value = '',
  preTrackVal = '',
  text = '',
}: UseTrackingParameters) {
  useEffect(() => {
    const {
      clickTrack = false,
      impressionTrack = false,
      impressionCount = false,
      isDCAEnabled = false,
      trackingContentPosition = '',
      trackingDescription = '',
      trackingOfferType = '',
      trackingTag = '',
      atCCeVar48 = '',
      compName = '',
      additionalTrackingVariables = '',
      merchandisingCategory = '',
      faicUrl = '',
      faicMCD = '',
    } = trackingProperties;

    const shouldEnableTracking = clickTrack || impressionTrack || custom_click_track_value.trim().length;
    const element = ref?.current;
    const isTrackingComplete = element?.classList?.contains('track-completed');

    if (!element) {
      return;
    }

    // See More / See Less (and similar toggles) reuse the same node. After the
    // first setup, still refresh the click-track value so the next click is tagged
    // correctly without re-running impression tracking.
    if (isTrackingComplete) {
      if (custom_click_track_value) {
        element.setAttribute('data-custom_click_track_value', custom_click_track_value);
      }
      return;
    }

    if (!shouldEnableTracking) {
      return;
    }

    Object.assign(ref.current.dataset, {
      isCoBrand: merchandisingCategory === constants.COBRAND_NON_POINTS_MATH,
      isImpressionTrack: impressionTrack,
      isClickTrack: clickTrack,
      trackingContentPosition,
      trackingDescription,
      trackingOfferType,
      trackingTag,
      atCCeVar48,
      compName,
      impressionCount,
      additionalTrackingVariables,
      isDCAEnabled,
      custom_click_track_value,
      faicHref: faicUrl,
      faicMcd: faicMCD,
    });

    ref.current?.classList?.add(
      ...clsx(clickTrack && 'custom_click_track', !impressionTrack && 'no-impression').split(' ')
    );
    if (!custom_click_track_value) {
      getClickTrackValue(isExternal ? 'external' : 'internal', trackingProperties, ref);
    }
    if (impressionTrack) {
      trackImpression(trackingProperties, text, preTrackVal);
    }
    ref.current?.classList?.add('track-completed');
  }, [ref, trackingProperties, isExternal, custom_click_track_value, preTrackVal, text]);
}
